import React from "react";
import styled, { css } from "styled-components";
import { mediaQueries, style } from "./style";

const columns = 12;
const gutterSpacing = style.vars.whitespace.regular;

const percentValue = (prop: string, value: number) => {
  return `${prop}: ${(100 / columns) * value}%`;
};

const allMediaQueries = [
  mediaQueries.xsmallMin,
  mediaQueries.smallMin,
  mediaQueries.mediumMin,
  mediaQueries.largeMin,
  mediaQueries.xlargeMin,
  mediaQueries.xxlargeMin,
  mediaQueries.xsmallMax,
  mediaQueries.smallMax,
  mediaQueries.mediumMax,
  mediaQueries.largeMax,
  mediaQueries.xlargeMax,
  mediaQueries.xxlargeMax,
];

const Grid = styled.div`
  display: flex;
  flex: 0 1 auto;
  flex-direction: row;
  flex-wrap: wrap;
  margin-left: -${gutterSpacing};
  margin-top: -${gutterSpacing};

  & > * {
    padding-left: ${gutterSpacing};
    padding-top: ${gutterSpacing};
    flex: 0 0 100%;
    max-width: 100%;
  }

  ${({ deepMatch }: Props) => {
    if (deepMatch) {
      return `
        & {
          > * {
            display: flex;
            flex-wrap: wrap;

            > * {
              flex: none;
              width: 100%;
            }
          }
        }
      `;
    }
  }}

  ${({ collapse }: Props) => {
    if (collapse) {
      return css`
        & {
          margin-top: 0;
          margin-left: 0;

          > * {
            padding-top: 0;
            padding-left: 0;
          }

          ${allMediaQueries.map(
            (breakpoint) => css`
              ${breakpoint} {
                margin-top: 0;
                margin-left: 0;

                > * {
                  padding-top: 0;
                  padding-left: 0;
                }
              }
            `
          )}
        }
      `;
    }
  }}

  ${({ largeGutter }: Props) => {
    if (largeGutter) {
      return css`
        & {
          margin-top: -${style.vars.whitespace.large};
          margin-left: -${style.vars.whitespace.large};

          > * {
            padding-left: ${style.vars.whitespace.large};
            padding-top: ${style.vars.whitespace.large};
          }

          ${allMediaQueries.map(
            (breakpoint) => css`
              ${breakpoint} {
                margin-top: -${style.vars.whitespace.large};
                margin-left: -${style.vars.whitespace.large};

                > * {
                  padding-left: ${style.vars.whitespace.large};
                  padding-top: ${style.vars.whitespace.large};
                }
              }
            `
          )}
        }
      `;
    }
  }}

  ${({ xlargeGutter }: Props) => {
    if (xlargeGutter) {
      return css`
        & {
          margin-top: -${style.vars.whitespace.xlarge};
          margin-left: -${style.vars.whitespace.xlarge};

          > * {
            padding-left: ${style.vars.whitespace.xlarge};
            padding-top: ${style.vars.whitespace.xlarge};
          }

          ${allMediaQueries.map(
            (breakpoint) => css`
              ${breakpoint} {
                margin-top: -${style.vars.whitespace.xlarge};
                margin-left: -${style.vars.whitespace.xlarge};

                > * {
                  padding-left: ${style.vars.whitespace.xlarge};
                  padding-top: ${style.vars.whitespace.xlarge};
                }
              }
            `
          )}
        }
      `;
    }
  }}
`;

const Col = styled.div<ColProps>`
  ${(props) =>
    props.sm > -1 &&
    css`
      ${mediaQueries.smallMin} {
        ${percentValue("flex-basis", props.sm)};
        ${percentValue("max-width", props.sm)};
      }
    `}

  ${(props) =>
    props.smOffset > -1 &&
    css`
      ${mediaQueries.smallMin} {
        ${percentValue("margin-left", props.smOffset)};
      }
    `}

  ${(props) =>
    props.md > -1 &&
    css`
      ${mediaQueries.mediumMin} {
        ${percentValue("flex-basis", props.md)};
        ${percentValue("max-width", props.md)};
      }
    `}

  ${(props) =>
    props.mdOffset > -1 &&
    css`
      ${mediaQueries.mediumMin} {
        ${percentValue("margin-left", props.mdOffset)};
      }
    `}

  ${(props) =>
    props.lg > -1 &&
    css`
      ${mediaQueries.largeMin} {
        ${percentValue("flex-basis", props.lg)};
        ${percentValue("max-width", props.lg)};
      }
    `}

  ${(props) =>
    props.lgOffset > -1 &&
    css`
      ${mediaQueries.largeMin} {
        ${percentValue("margin-left", props.lgOffset)};
      }
    `}

  ${(props) =>
    props.xlg > -1 &&
    css`
      ${mediaQueries.xlargeMin} {
        ${percentValue("flex-basis", props.xlg)};
        ${percentValue("max-width", props.xlg)};
      }
    `}

  ${(props) =>
    props.xlgOffset > -1 &&
    css`
      ${mediaQueries.xlargeMin} {
        ${percentValue("margin-left", props.xlgOffset)};
      }
    `}

  ${(props) =>
    props.xxlg > -1 &&
    css`
      ${mediaQueries.xxlargeMin} {
        ${percentValue("flex-basis", props.xxlg)};
        ${percentValue("max-width", props.xxlg)};
      }
    `}

  ${(props) =>
    props.xxlgOffset > -1 &&
    css`
      ${mediaQueries.xxlargeMin} {
        ${percentValue("margin-left", props.xxlgOffset)};
      }
    `}
`;

type Props = React.PropsWithChildren<{
  className?: string;
  deepMatch?: boolean;
  collapse?: boolean;
  largeGutter?: boolean;
  xlargeGutter?: boolean;
}>;

type ColProps = React.PropsWithChildren<{
  children: React.ReactNode;
  className: string;
  sm: number;
  md: number;
  lg: number;
  xlg: number;
  xxlg: number;
  smOffset: number;
  mdOffset: number;
  lgOffset: number;
  xlgOffset: number;
  xxlgOffset: number;
}>;

// eslint-disable-next-line @typescript-eslint/ban-types
export class FlexboxGrid extends React.Component<Props, {}> {
  public static Col = ({
    children,
    className,
    sm = -1,
    md = -1,
    lg = -1,
    xlg = -1,
    xxlg = -1,
    smOffset = -1,
    mdOffset = -1,
    lgOffset = -1,
    xlgOffset = -1,
    xxlgOffset = -1,
  }: Partial<ColProps>) => (
    <Col
      className={className ?? ""}
      sm={sm}
      md={md}
      lg={lg}
      xlg={xlg}
      xxlg={xxlg}
      smOffset={smOffset}
      mdOffset={mdOffset}
      lgOffset={lgOffset}
      xlgOffset={xlgOffset}
      xxlgOffset={xxlgOffset}
    >
      {children}
    </Col>
  );

  public render() {
    return (
      <Grid {...this.props}>
        {React.Children.map(this.props.children, (child) => React.cloneElement(child as React.ReactElement<any>))}
      </Grid>
    );
  }
}

Object.defineProperty(FlexboxGrid.Col, "name", {
  value: "FlexboxGrid.Col",
});
